<template>
  <div
    class="play-dropdown"
    :class="[
      `play-dropdown--${align}`,
      `play-dropdown--${placement}`,
      { 'is-open': open, 'play-dropdown--block': block },
    ]"
  >
    <PlayButton
      ref="trigger"
      class="play-dropdown__trigger"
      :variant="variant"
      :size="size"
      :block="block"
      :disabled="disabled"
      aria-haspopup="menu"
      :aria-expanded="open ? 'true' : 'false'"
      @click="toggle"
      @keydown="onTriggerKeydown"
    >
      <slot name="trigger">
        {{ text }}
      </slot>
      <span
        v-if="!noCaret"
        class="play-dropdown__caret"
        aria-hidden="true"
      >
        <svg viewBox="0 0 24 24"><path d="M6 9l6 6 6-6" /></svg>
      </span>
    </PlayButton>

    <transition name="play-dropdown-anim">
      <div
        v-show="open"
        ref="menu"
        class="play-dropdown__menu"
        :class="{ 'play-dropdown__menu--scrollable': scrollable }"
        :style="{ '--dd-max-h': `${maxHeight}px` }"
        role="menu"
        tabindex="-1"
        @keydown="onMenuKeydown"
      >
        <slot />
      </div>
    </transition>
  </div>
</template>

<script>
import PlayButton from '@/components/play/PlayButton.vue'

/**
 * PlayDropdown — trigger + menu for the Play system.
 *
 * Token-driven (see src/styles/tokens.scss): no hardcoded colors. Replaces
 * bootstrap-vue's <b-dropdown>. The trigger is a <PlayButton>; `text` is the
 * simple label, or the `trigger` slot fills it (the old `#button-content`).
 * The default slot holds <PlayDropdownItem>s. Closes on outside-click, Esc, and
 * item-select; full keyboard support (arrows / Home / End / Enter / Esc).
 *
 * Coordination is via provide/inject (NOT $parent) so it is Vue 3-safe: items
 * register for keyboard navigation and ask the menu to close on select.
 *
 * VUE3-MIGRATION: positioning is CSS-absolute (no Popper/portal). Transition
 * classes use Vue 2 names (`-enter`/`-leave`); Vue 3 renames to
 * `-enter-from`/`-leave-from` (see the <style> block). provide/inject + the
 * props/slots contract are already Vue 3-safe.
 */
export default {
  name: 'PlayDropdown',
  components: { PlayButton },
  provide() {
    return {
      playDropdown: this.api,
    }
  },
  props: {
    /** Simple trigger label (ignored when the `trigger` slot is used) */
    text: {
      type: String,
      default: '',
    },
    /** Trigger button color role (PlayButton variants) */
    variant: {
      type: String,
      default: 'ghost',
      validator: v => ['primary', 'highlight', 'secondary', 'ghost', 'plain'].includes(v),
    },
    size: {
      type: String,
      default: 'md',
    },
    /** Menu horizontal alignment to the trigger */
    align: {
      type: String,
      default: 'start',
      validator: v => ['start', 'end'].includes(v),
    },
    /** Open above or below the trigger */
    placement: {
      type: String,
      default: 'bottom',
      validator: v => ['bottom', 'top'].includes(v),
    },
    /** Hide the caret chevron */
    noCaret: {
      type: Boolean,
      default: false,
    },
    /** Full-width trigger */
    block: {
      type: Boolean,
      default: false,
    },
    /** Cap the menu height and scroll past it */
    scrollable: {
      type: Boolean,
      default: false,
    },
    maxHeight: {
      type: Number,
      default: 280,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      open: false,
      items: [],
      // Shared contract handed to descendants via inject.
      api: {
        register: this.register,
        unregister: this.unregister,
        select: this.onSelect,
      },
    }
  },
  beforeDestroy() {
    document.removeEventListener('mousedown', this.onOutside, true)
  },
  methods: {
    // ── Item registry (for keyboard navigation) ──
    register(item) {
      this.items.push(item)
    },
    unregister(item) {
      const i = this.items.indexOf(item)
      if (i !== -1) this.items.splice(i, 1)
    },
    onSelect() {
      this.closeMenu()
    },

    // ── Open / close ──
    toggle() {
      if (this.disabled) return
      if (this.open) this.closeMenu()
      else this.openMenu()
    },
    openMenu() {
      if (this.open) return
      this.open = true
      this.$emit('show')
      document.addEventListener('mousedown', this.onOutside, true)
      this.$nextTick(() => {
        this.focusItem(this.firstEnabled())
        this.$emit('shown')
      })
    },
    closeMenu(focusTrigger = true) {
      if (!this.open) return
      this.open = false
      document.removeEventListener('mousedown', this.onOutside, true)
      this.$emit('hide')
      if (focusTrigger) this.focusTrigger()
    },
    onOutside(e) {
      if (!this.$el.contains(e.target)) this.closeMenu(false)
    },

    // ── Focus helpers ──
    focusTrigger() {
      const el = this.$refs.trigger && this.$refs.trigger.$el
      if (el) el.focus()
    },
    focusItem(i) {
      const item = this.items[i]
      if (item && item.$el) item.$el.focus()
    },
    firstEnabled() {
      return this.items.findIndex(it => !it.disabled)
    },
    lastEnabled() {
      for (let i = this.items.length - 1; i >= 0; i -= 1) {
        if (!this.items[i].disabled) return i
      }
      return -1
    },
    nextEnabled(from, dir) {
      const n = this.items.length
      if (!n) return -1
      for (let step = 1; step <= n; step += 1) {
        const i = (from + dir * step + n * step) % n
        if (!this.items[i].disabled) return i
      }
      return from
    },
    currentIndex() {
      return this.items.findIndex(it => it.$el === document.activeElement)
    },

    // ── Keyboard ──
    onTriggerKeydown(e) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault()
        if (!this.open) this.openMenu()
        else this.focusItem(e.key === 'ArrowDown' ? this.firstEnabled() : this.lastEnabled())
      } else if (e.key === 'Escape' && this.open) {
        e.preventDefault()
        this.closeMenu()
      }
    },
    onMenuKeydown(e) {
      const current = this.currentIndex()
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        this.focusItem(this.nextEnabled(current, 1))
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        this.focusItem(this.nextEnabled(current, -1))
      } else if (e.key === 'Home') {
        e.preventDefault()
        this.focusItem(this.firstEnabled())
      } else if (e.key === 'End') {
        e.preventDefault()
        this.focusItem(this.lastEnabled())
      } else if (e.key === 'Escape' || e.key === 'Tab') {
        this.closeMenu(e.key === 'Escape')
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.play-dropdown {
  position: relative;
  display: inline-block;

  &--block { display: block; }

  /* ── Caret ── */
  &__caret {
    display: inline-flex;
    margin-left: 2px;
    transition: transform var(--dur-fast) var(--ease-bounce);

    svg {
      width: 16px;
      height: 16px;
      fill: none;
      stroke: currentColor;
      stroke-width: 3;
      stroke-linecap: round;
      stroke-linejoin: round;
    }
  }

  &.is-open &__caret { transform: rotate(180deg); }

  /* ── Menu ── */
  &__menu {
    position: absolute;
    z-index: 1040;
    min-width: 200px;
    max-width: 320px;
    padding: 7px;
    background: var(--surface-card);
    border: var(--border-w) solid var(--border-ink);
    border-radius: var(--radius-lg);
    box-shadow: var(--shadow-hard);
  }

  /* placement */
  &--bottom &__menu { top: calc(100% + 8px); }
  &--top &__menu { bottom: calc(100% + 8px); }

  /* alignment */
  &--start &__menu { left: 0; }
  &--end &__menu { right: 0; }

  &__menu--scrollable {
    max-height: var(--dd-max-h, 280px);
    overflow-y: auto;
  }
}

/* ── Transition (Vue 2 class names — see VUE3-MIGRATION) ── */
.play-dropdown-anim-enter-active,
.play-dropdown-anim-leave-active {
  transition: opacity var(--dur-fast) var(--ease-out), transform var(--dur-fast) var(--ease-bounce);
}

.play-dropdown-anim-enter,
.play-dropdown-anim-leave-to {
  opacity: 0;
  transform: translateY(-6px) scale(.97);
}

.play-dropdown--top .play-dropdown-anim-enter,
.play-dropdown--top .play-dropdown-anim-leave-to {
  transform: translateY(6px) scale(.97);
}

@media (prefers-reduced-motion: reduce) {
  .play-dropdown__caret,
  .play-dropdown-anim-enter-active,
  .play-dropdown-anim-leave-active { transition: none; }

  .play-dropdown-anim-enter,
  .play-dropdown-anim-leave-to { transform: none; }
}
</style>
